import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { MessageCircle, X, Send, Minimize2, Maximize2, Dot } from 'lucide-react';
import { useLocation } from 'react-router-dom';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

// Genere un visitor_id stable par session
function getVisitorId() {
  let id = sessionStorage.getItem('ose_visitor_id');
  if (!id) {
    id = 'v-' + Math.random().toString(36).slice(2) + Date.now().toString(36);
    sessionStorage.setItem('ose_visitor_id', id);
  }
  return id;
}

export default function LiveChatWidget() {
  const { user } = useAuth();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [minimized, setMinimized] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [connected, setConnected] = useState(false);
  const [unread, setUnread] = useState(0);
  const [adminTyping, setAdminTyping] = useState(false);
  const [fallbackMode, setFallbackMode] = useState(false);
  const [email, setEmail] = useState(user?.email || '');
  const wsRef = useRef(null);
  const messagesEndRef = useRef(null);
  const visitorId = getVisitorId();

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => { scrollToBottom(); }, [messages, scrollToBottom]);

  // Ne pas afficher dans l'admin ou la page livreur
  const hiddenPaths = ['/admin', '/livreur'];
  const isHidden = hiddenPaths.some(p => location.pathname.startsWith(p));

  // Connexion WebSocket
  useEffect(() => {
    const wsUrl = BACKEND
      .replace('https://', 'wss://')
      .replace('http://', 'ws://');
    const params = new URLSearchParams({
      page: location.pathname,
      ...(user?.id ? { user_id: user.id } : {}),
      ...(user?.nom ? { user_name: user.nom } : {}),
    });
    const ws = new WebSocket(`${wsUrl}/api/ws/chat/visitor/${visitorId}?${params}`);
    wsRef.current = ws;

    ws.onopen = () => setConnected(true);
    ws.onclose = () => setConnected(false);
    ws.onerror = () => setConnected(false);
    ws.onmessage = (e) => {
      try {
        const data = JSON.parse(e.data);
        if (data.type === 'message') {
          const msg = { sender: 'admin', body: data.body, time: data.created_at };
          setMessages(prev => [...prev, msg]);
          if (!open || minimized) setUnread(u => u + 1);
        } else if (data.type === 'admin_typing') {
          setAdminTyping(true);
          setTimeout(() => setAdminTyping(false), 3000);
        }
      } catch (err) { if (process.env.NODE_ENV !== 'production') console.error(err); }
    };

    // Ping toutes les 25s pour maintenir la connexion
    const ping = setInterval(() => {
      if (ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify({ type: 'ping' }));
    }, 25000);

    return () => { clearInterval(ping); ws.close(); };
  // eslint-disable-next-line
  }, []);

  // Signaler les changements de page
  useEffect(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'page_change', page: location.pathname }));
    }
  }, [location.pathname]);

  const sendMessage = useCallback(() => {
    const body = input.trim();
    if (!body) return;
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'message', body }));
      setMessages(prev => [...prev, { sender: 'visitor', body, time: new Date().toISOString() }]);
      setInput('');
      setFallbackMode(false);
    } else {
      // Fallback: afficher formulaire email
      setFallbackMode(true);
    }
  }, [input]);

  const handleOpen = () => {
    setOpen(true);
    setMinimized(false);
    setUnread(0);
    if (messages.length === 0) {
      setMessages([{
        sender: 'system',
        body: 'Bonjour ! 👋 Comment puis-je vous aider ?',
        time: new Date().toISOString(),
      }]);
    }
  };

  const timeLabel = (iso) => {
    try {
      return new Date(iso).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
    } catch { return ''; }
  };

  // Return null if hidden (after all hooks are called)
  if (isHidden) return null;

  return (
    <div
      data-testid="livechat-widget"
      style={{
        position: 'fixed',
        bottom: 'calc(env(safe-area-inset-bottom, 0px) + 90px)',
        right: 16,
        zIndex: 9000,
        fontFamily: 'Manrope, sans-serif',
      }}
    >
      {/* Bulle d'ouverture */}
      {!open && (
        <button
          data-testid="livechat-open-btn"
          onClick={handleOpen}
          style={{
            width: 56, height: 56,
            borderRadius: '50%',
            background: 'linear-gradient(135deg, #5DBCBF 0%, #0B4B4E 100%)',
            boxShadow: '0 8px 28px rgba(93,188,191,0.45)',
            border: 'none',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'transform 0.2s',
            position: 'relative',
          }}
          onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.08)'}
          onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}
        >
          <MessageCircle size={24} color="#FFFFFF" />
          {unread > 0 && (
            <span style={{
              position: 'absolute', top: -4, right: -4,
              background: '#F4A7B2', color: '#0B1E20',
              width: 20, height: 20, borderRadius: '50%',
              fontSize: 11, fontWeight: 700,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>{unread}</span>
          )}
        </button>
      )}

      {/* Fenêtre chat */}
      {open && (
        <div style={{
          width: 'min(340px, calc(100vw - 32px))',
          borderRadius: 20,
          overflow: 'hidden',
          boxShadow: '0 24px 64px rgba(26,26,46,0.22)',
          border: '1px solid rgba(93,188,191,0.2)',
          background: '#FEFAF5',
          display: 'flex',
          flexDirection: 'column',
          maxHeight: minimized ? 56 : 480,
          transition: 'max-height 0.3s ease',
        }}>
          {/* Header */}
          <div style={{
            background: 'linear-gradient(135deg, #5DBCBF 0%, #0B4B4E 100%)',
            padding: '14px 16px',
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            cursor: 'pointer',
          }}
            onClick={() => setMinimized(!minimized)}
          >
            <div style={{
              width: 36, height: 36, borderRadius: '50%',
              background: 'rgba(255,255,255,0.2)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <MessageCircle size={18} color="#FFFFFF" />
            </div>
            <div style={{ flex: 1 }}>
              <p style={{ color: '#FFFFFF', fontSize: 14, fontWeight: 700, margin: 0, lineHeight: 1.3 }}>
                Show Événement
              </p>
              <p style={{ color: 'rgba(255,255,255,0.75)', fontSize: 11, margin: 0 }}>
                {connected ? '● En ligne' : '○ Hors ligne'}
              </p>
            </div>
            <div style={{ display: 'flex', gap: 4 }}>
              {minimized
                ? <Maximize2 size={15} color="rgba(255,255,255,0.8)" />
                : <Minimize2 size={15} color="rgba(255,255,255,0.8)" />}
              <button
                onClick={(e) => { e.stopPropagation(); setOpen(false); }}
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2 }}
              >
                <X size={15} color="rgba(255,255,255,0.8)" />
              </button>
            </div>
          </div>

          {!minimized && (
            <>
              {/* Messages */}
              <div style={{
                flex: 1,
                overflowY: 'auto',
                padding: '14px 14px 8px',
                display: 'flex',
                flexDirection: 'column',
                gap: 8,
                minHeight: 0,
                maxHeight: 320,
              }}>
                {messages.map((msg, i) => (
                  <div key={`msg-${i}`} style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: msg.sender === 'visitor' ? 'flex-end' : 'flex-start',
                  }}>
                    <div style={{
                      maxWidth: '82%',
                      padding: '8px 12px',
                      borderRadius: msg.sender === 'visitor'
                        ? '16px 16px 4px 16px'
                        : msg.sender === 'system'
                        ? '12px'
                        : '16px 16px 16px 4px',
                      background: msg.sender === 'visitor'
                        ? 'linear-gradient(135deg, #5DBCBF 0%, #0B4B4E 100%)'
                        : msg.sender === 'system'
                        ? 'rgba(93,188,191,0.1)'
                        : '#FFFFFF',
                      color: msg.sender === 'visitor' ? '#FFFFFF'
                        : msg.sender === 'system' ? '#0B4B4E'
                        : '#1A1A2E',
                      fontSize: 13,
                      lineHeight: 1.5,
                      boxShadow: msg.sender !== 'visitor' ? '0 2px 8px rgba(0,0,0,0.06)' : 'none',
                      border: msg.sender === 'system' ? '1px solid rgba(93,188,191,0.2)' : 'none',
                    }}>
                      {msg.body}
                    </div>
                    <span style={{ fontSize: 10, color: 'rgba(26,26,46,0.35)', marginTop: 2 }}>
                      {timeLabel(msg.time)}
                    </span>
                  </div>
                ))}
                {adminTyping && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <div style={{ padding: '8px 14px', borderRadius: '16px 16px 16px 4px', background: '#FFFFFF', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
                      <span style={{ color: '#5DBCBF', fontSize: 18, letterSpacing: 2 }}>···</span>
                    </div>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* Fallback email */}
              {fallbackMode && (
                <div style={{ padding: '8px 14px', background: 'rgba(244,167,178,0.08)', borderTop: '1px solid rgba(244,167,178,0.2)' }}>
                  <p style={{ fontSize: 11, color: '#C58B19', margin: '0 0 6px' }}>
                    ℹ️ Connexion perdue. Laissez votre email pour qu'on vous réponde :
                  </p>
                  <input
                    type="email"
                    placeholder="votre@email.fr"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    style={{
                      width: '100%', padding: '7px 10px', borderRadius: 10, fontSize: 13,
                      border: '1px solid var(--border)', background: '#FFFFFF', color: '#1A1A2E',
                    }}
                  />
                </div>
              )}

              {/* Input */}
              <div style={{
                padding: '10px 12px',
                borderTop: '1px solid rgba(93,188,191,0.15)',
                display: 'flex',
                gap: 8,
                background: '#FFFFFF',
              }}>
                <input
                  data-testid="livechat-input"
                  type="text"
                  placeholder="Écrivez votre message…"
                  value={input}
                  onChange={e => setInput(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && sendMessage()}
                  style={{
                    flex: 1,
                    padding: '9px 12px',
                    borderRadius: 12,
                    border: '1px solid rgba(93,188,191,0.25)',
                    background: '#FEFAF5',
                    fontSize: 13,
                    color: '#1A1A2E',
                    outline: 'none',
                  }}
                />
                <button
                  data-testid="livechat-send-btn"
                  onClick={sendMessage}
                  style={{
                    width: 40, height: 40,
                    borderRadius: 12,
                    background: 'linear-gradient(135deg, #5DBCBF 0%, #0B4B4E 100%)',
                    border: 'none',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  <Send size={16} color="#FFFFFF" />
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
